import { useEffect, useRef, useState } from 'react'
import { SERIES, type Series } from '../api'
import { SERIES_LABEL } from '../domain/labels'
import { createStore, useStore } from '../state/store'
import { CheckIcon, ChevronIcon } from './icons'
import { SeriesDot } from './SeriesDot'
import './series-filter.css'

/**
 * Which series the lists show: one button and a short menu, where there used
 * to be a row of chips. It is set a few times a week and read the rest of the
 * time, so it takes one line rather than a row of its own. Shared by the
 * pipeline and the LinkedIn list, and kept across a trip into an idea.
 */
export type SeriesView = Series | 'all'

export const seriesViewStore = createStore<SeriesView>('all')

export const useSeriesView = () => useStore(seriesViewStore)

export function SeriesFilter({ counts }: { counts: Record<SeriesView, number> }) {
  const view = useSeriesView()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="series-filter" ref={root}>
      <button
        type="button"
        className="series-filter-button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((shown) => !shown)}
      >
        {view !== 'all' && <SeriesDot series={view} />}
        {view === 'all' ? 'All series' : SERIES_LABEL[view]}
        <ChevronIcon size={15} />
      </button>
      {open && (
        <div className="series-filter-menu" role="menu">
          {(['all', ...SERIES] as SeriesView[]).map((value) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={view === value}
              onClick={() => {
                seriesViewStore.set(value)
                setOpen(false)
              }}
            >
              {value === 'all' ? <span className="series-filter-spacer" /> : <SeriesDot series={value} />}
              <span className="series-filter-label">{value === 'all' ? 'All series' : SERIES_LABEL[value]}</span>
              <span className="series-filter-count mono">{counts[value]}</span>
              <span className="series-filter-check">{view === value && <CheckIcon size={16} />}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** How many of `ideas` each entry in the menu would show. */
export function seriesCounts(ideas: readonly { series: Series }[]): Record<SeriesView, number> {
  const counts = { all: ideas.length } as Record<SeriesView, number>
  for (const series of SERIES) counts[series] = ideas.filter((idea) => idea.series === series).length
  return counts
}
